import styles from "./style.module.scss";
import ShowAnswerUnlimitedCta from "../questionCta/showAnswerUnlimitedCta";
import ShowAnswerWithCreditCta from "../questionCta/showAnswerWithCreditCta";
import GoUnlimitedCta from "../questionCta/goUnlimitedCta";
import SignupSocialCta from "../questionCta/signupSocialCta";
import { useTranslation } from "next-i18next";
import { useAppContext } from "@kunduz/feature-website-context";
import type { QuestionIndexing } from "@kunduz/data-access-qi-api";
import { trackEvent } from "@kunduz/utility-analytics";
import { LoadingSkeleton, Panel } from "@kunduz/ui";
import {
  UnlockedAnswerImage,
  UnlockedAnswerText,
  useIsQuestionInInbox,
} from "@kunduz/feature-question-indexing";
import classNames from "classnames";

const QuestionAnswer = (props: Props) => {
  const { t } = useTranslation("questionIndexing");
  const { student, toast } = useAppContext();
  const { questionInInbox, questionInInboxLoading, moveToInbox } =
    useIsQuestionInInbox(student?.data?.id, props.question.id, props.lang);

  /**
   * callback for show answer click
   */
  function onShowAnswerClicked() {
    const plan_type = !student.data?.primary_package
      ? "freemium"
      : student.data?.primary_package?.package.is_recurring
      ? "monthly"
      : "unlimited";

    trackEvent("Question Indexing Show Answer Button Clicked", {
      course: props.question?.course?.name,
      subject: props.question?.subject?.name,
      page_slug: props.question?.slug,
      initial_answer_credit: t("question_indexing_answer_view_credit"),
      destination_url: props.question.branch_io_link,
      cta_button_text: t("question_indexing_show_answer_banner_CTA"),
      plan_type,
    });

    triggerMoveToInbox();
  }

  async function triggerMoveToInbox() {
    try {
      // SUG questions has question id with a different name
      const sugNormalizedQuestionId: number =
        props.question.question ||
        (props.question as unknown as QuestionIndexing.UsQiQuestion)
          .question_id;

      await moveToInbox(sugNormalizedQuestionId);

      student.refresh();
      trackEvent("Question Indexing Question Answer Viewed");
    } catch (error) {
      toast.toastError(error.message);
    }
  }

  // unauth state
  function printUnAuthStatus() {
    return (
      <Panel data-testid="answer-unlock-component" className={styles.container}>
        <p className={styles.answerTitle} id="answer-title">
          <StupidQuestionMark />
          {t("question_indexing_answer_component_title")}
        </p>
        <SignupSocialCta />
      </Panel>
    );
  }

  // user has unlimited package state
  function printShowAnswerUnlimited() {
    return (
      <Panel className={styles.container}>
        <p className={styles.answerTitle} id="answer-title">
          <StupidQuestionMark />
          {t("question_indexing_answer_component_title")}
        </p>
        <ShowAnswerUnlimitedCta onShowClicked={onShowAnswerClicked} />
      </Panel>
    );
  }

  // user has limited package state, yes credits
  function printShowAnswerLimited() {
    return (
      <Panel className={styles.container}>
        <p className={styles.answerTitle} id="answer-title">
          <StupidQuestionMark />
          {t("question_indexing_answer_component_title")}
        </p>
        <ShowAnswerWithCreditCta
          onShowClicked={onShowAnswerClicked}
          credits={student?.data?.limits?.questions?.remained}
        />
      </Panel>
    );
  }

  // user has limited package state, no credits
  function printShowGoUnlimited() {
    return (
      <Panel className={styles.container}>
        <p className={styles.answerTitle} id="answer-title">
          <StupidQuestionMark />
          {t("question_indexing_answer_component_title")}
        </p>
        <GoUnlimitedCta question={props.question} />
      </Panel>
    );
  }

  // unlocked state
  function printUnlockedStatus() {
    return (
      <Panel className={classNames(styles.unlockedPanel, styles.container)}>
        <p className={styles.answerTitle} id="answer-title">
          <StupidQuestionMark />
          {t("question_indexing_answer_component_title")}
        </p>
        <div className={styles.questionAnswerWrapper}>
          <UnlockedAnswerImage question={props.question} />
        </div>

        <UnlockedAnswerText question={props.question} />
      </Panel>
    );
  }

  function printAnswer() {
    if (student.loading || questionInInboxLoading) {
      return (
        <Panel className={styles.container}>
          <LoadingSkeleton style={{ height: 260, width: "100%" }} />
        </Panel>
      );
    }

    /*return (
      <>
        {printUnAuthStatus()}
        {printShowAnswerUnlimited()}
        {printShowAnswerLimited()}
        {printShowGoUnlimited()}
        {printUnlockedStatus()}
      </>
    );*/

    if (questionInInbox) {
      return printUnlockedStatus();
    }

    // logged out ise uye ol
    if (!student.data) {
      return printUnAuthStatus();
    }
    // unlimited ise direk unlock cta
    else if (student?.data?.limits?.questions?.is_unlimited) {
      return printShowAnswerUnlimited();
    }
    // limited ve limit varsa harca ac
    else if (student?.data?.limits?.questions?.remained > 0) {
      return printShowAnswerLimited();
    }
    // limited ve limit yoksa go unlimited
    else {
      return printShowGoUnlimited();
    }
  }

  return (
    <>
      <div id="answer-anchor" />
      {printAnswer()}
    </>
  );
};

interface Props {
  question: QuestionIndexing.TrQiQuestion;
  lang: string;
  isAuth?: boolean;
  url: string;
}

function StupidQuestionMark() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.7042 0.0177809C11.9529 0.117578 10.3646 1.17242 9.52118 2.7957C9.43779 2.9562 9.40708 3.0022 9.38747 2.99595C9.37349 2.9915 9.28118 2.95981 9.18235 2.92553C8.94916 2.84466 8.73036 2.78981 8.45733 2.74376C7.22214 2.53553 5.9733 2.80006 4.92453 3.49206C2.97542 4.77814 2.09572 7.26544 2.77153 9.57953C2.81044 9.71273 2.84545 9.83284 2.84937 9.84644C2.85454 9.86442 2.81254 9.8958 2.6952 9.96159C1.6282 10.5598 0.793402 11.5404 0.354823 12.7107C-0.0985526 13.9205 -0.118052 15.2509 0.299776 16.4694C0.570042 17.2576 0.996667 17.9424 1.58026 18.525C1.83886 18.7831 2.05995 18.9642 2.36747 19.1699L2.59165 19.3198L2.58278 19.3754C2.55867 19.5268 2.54001 19.972 2.54901 20.1811C2.59525 21.2541 2.89128 22.1872 3.45626 23.0405C3.66384 23.354 3.84017 23.5679 4.12651 23.8534C4.56659 24.2923 4.98292 24.5897 5.51419 24.8446C6.14617 25.1477 6.77669 25.3011 7.48703 25.3243C8.06396 25.3432 8.60082 25.2662 9.15019 25.0858C9.29149 25.0394 9.40919 25.0014 9.41177 25.0014C9.41433 25.0014 9.45136 25.071 9.49405 25.1561C10.1179 26.3993 11.2097 27.3464 12.4946 27.7589C12.8518 27.8735 13.2849 27.9584 13.612 27.9777C13.698 27.9828 13.8129 27.9903 13.8674 27.9943C14.0154 28.0053 14.5058 27.9736 14.7397 27.9379C16.3506 27.6923 17.7437 26.6506 18.5171 25.1133C18.5536 25.0407 18.5821 25.0016 18.5948 25.0065C18.6059 25.0107 18.7265 25.0503 18.8628 25.0944C19.1324 25.1817 19.4498 25.2511 19.7683 25.2924C20.0157 25.3244 20.6458 25.329 20.8777 25.3005C21.8586 25.1796 22.6925 24.8318 23.4558 24.225C23.674 24.0516 24.0567 23.6695 24.2353 23.4467C25.1468 22.3097 25.5663 20.8578 25.4081 19.3875L25.3993 19.3061L25.519 19.2358C25.6922 19.134 26.0268 18.8788 26.2399 18.6858C26.863 18.1216 27.3636 17.3701 27.6588 16.5556C28.156 15.1839 28.0853 13.6342 27.4651 12.3098C27.0061 11.3296 26.2284 10.4883 25.2956 9.96327C25.2076 9.91369 25.1355 9.86453 25.1355 9.85406C25.1355 9.84358 25.1523 9.78555 25.1729 9.72511C25.2767 9.4197 25.3783 8.92905 25.418 8.54047C25.4427 8.29881 25.4429 7.70111 25.4183 7.47016C25.3419 6.75139 25.1656 6.13853 24.8599 5.52834C24.0783 3.9683 22.6195 2.91811 20.95 2.71344C20.2248 2.62453 19.45 2.70448 18.7724 2.93816C18.6715 2.97294 18.5826 3.00141 18.5748 3.00141C18.5671 3.00141 18.5567 2.98559 18.5517 2.96625C18.5467 2.94691 18.4888 2.83266 18.4232 2.71234C17.9035 1.76059 17.1521 1.01983 16.2292 0.549375C15.5947 0.225922 14.9659 0.0563433 14.2547 0.0168589C14.1075 0.00868705 13.9695 0.00292155 13.948 0.00404655C13.9265 0.00518717 13.8168 0.011359 13.7042 0.0177809ZM18.659 8.71756C18.7297 8.74194 18.8375 8.79509 18.8986 8.83567C19.3017 9.10367 19.4539 9.63389 19.256 10.0813C19.2355 10.1276 17.8967 12.1485 16.281 14.5722C13.0483 19.4211 13.2731 19.1 13.0224 19.2257C12.8648 19.3047 12.7345 19.3353 12.5574 19.335C12.3066 19.3346 12.1247 19.2673 11.9146 19.097C11.8513 19.0458 11.0647 18.2668 10.1666 17.3659C8.95866 16.1544 8.5201 15.7049 8.48185 15.6395C8.35216 15.4175 8.30738 15.1433 8.36124 14.9012C8.4833 14.3525 9.04069 14.0039 9.57663 14.141C9.6891 14.1697 9.8286 14.232 9.91977 14.2941C9.94293 14.3099 10.5111 14.8699 11.1825 15.5387C11.8538 16.2075 12.4055 16.7521 12.4084 16.7491C12.4114 16.7461 13.5672 15.0124 14.977 12.8964C16.3868 10.7804 17.5705 9.0162 17.6074 8.97587C17.7393 8.83184 17.966 8.71151 18.1786 8.67276C18.3082 8.64914 18.517 8.66859 18.659 8.71756Z"
        fill="#205EB8"
      />
    </svg>
  );
}

export default QuestionAnswer;
